import { useCallback, useMemo } from "react";
import { Button, Flex, Icon, Icons, type TableColumnType } from "@ir/ui";
import {
  MasterDataDeductionType,
  type IMasterDataDeductionType,
  type MasterDataDeductionType as MasterDataDeductionTypeEntity,
} from "@src/domain/entities/masterData";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
  createMasterDataLinkedSelectionColumn,
  renderMasterDataLinkedNameCell,
  renderMasterDataTextCell,
} from "./masterDataTable.shared";
import { MasterDataEditActionCell } from "./MasterDataEditActionCell";

export interface MasterDataDeductionSubTypeTarget {
  typeId: number;
  typeName: string;
  count: number;
}

export interface MasterDataDeductionTypeRow extends IMasterDataDeductionType {
  key: string;
}

function mapTypesToRows(
  types: MasterDataDeductionTypeEntity[],
): MasterDataDeductionTypeRow[] {
  return types.map((entity) => ({
    ...MasterDataDeductionType.toJSON(entity),
    key: String(entity.typeId),
  }));
}

function renderCountButton(
  target: MasterDataDeductionSubTypeTarget,
  onView: (target: MasterDataDeductionSubTypeTarget) => void,
) {
  const viewLabel = `View(${target.count})`;
  return (
    <Flex align="center" justifyContent="start">
      <Button
        size="small"
        aria-label={`${viewLabel} sub deduction types for ${target.typeName}`}
        onPress={() => onView(target)}
      >
        {viewLabel}
      </Button>
    </Flex>
  );
}

interface BuildColumnsArgs {
  onEdit: (entity: MasterDataDeductionTypeEntity) => void;
  onViewSubTypes: (target: MasterDataDeductionSubTypeTarget) => void;
  onPreviewMetadata: (entity: MasterDataDeductionTypeEntity) => void;
  typesById: Map<number, MasterDataDeductionTypeEntity>;
}

function buildColumns({
  onEdit,
  onViewSubTypes,
  onPreviewMetadata,
  typesById,
}: BuildColumnsArgs): TableColumnType<MasterDataDeductionTypeRow>[] {
  return [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 220,
      fixed: "left",
      cellRender: (value: unknown, record: MasterDataDeductionTypeRow) =>
        renderMasterDataLinkedNameCell(value, record),
    },
    {
      title: "Deduction Type Key",
      dataIndex: "deductionTypeKey",
      key: "deductionTypeKey",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 200,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 180,
      cellRender: renderMasterDataTextCell,
    },
    {
      title: "Deduction Sub-Types",
      dataIndex: "subTypeCount",
      key: "subTypeCount",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 180,
      cellRender: (_value: unknown, record: MasterDataDeductionTypeRow) =>
        renderCountButton(
          {
            typeId: record.typeId,
            typeName: record.name,
            count: record.subTypeCount,
          },
          onViewSubTypes,
        ),
    },
    {
      title: "Metadata",
      dataIndex: "metadata",
      key: "metadata",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 110,
      cellRender: (_value: unknown, record: MasterDataDeductionTypeRow) => {
        const entity = typesById.get(record.typeId);
        if (!entity) return null;
        return entity.metadata != null ? (
          <Flex align="center" justifyContent="center">
            <Button
              size="small"
              variant="outline"
              color="grey"
              aria-label={`View metadata for ${entity.name}`}
              onPress={() => onPreviewMetadata(entity)}
            >
              <Icon name={Icons.Eye} size="small" />
            </Button>
          </Flex>
        ) : (
          <span className="text-grey-500">—</span>
        );
      },
    },

    {
      title: "Actions",
      dataIndex: "typeId",
      key: "actions",
      align: "center",
      fixed: "right",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 80,
      cellRender: (value: unknown) => (
        <MasterDataEditActionCell
          entityId={value}
          entitiesById={typesById}
          onEdit={onEdit}
        />
      ),
    },
  ];
}

interface MasterDataDeductionTypesTableProps {
  deductionTypes: MasterDataDeductionTypeEntity[];
  search: string;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  onEdit: (entity: MasterDataDeductionTypeEntity) => void;
  onViewSubTypes: (target: MasterDataDeductionSubTypeTarget) => void;
  onPreviewMetadata: (entity: MasterDataDeductionTypeEntity) => void;
  showSelection: boolean;
  selectedIds: ReadonlySet<number>;
  onSelectionChange: (selectedIds: Set<number>) => void;
}

export const MasterDataDeductionTypesTable = ({
  deductionTypes,
  search,
  setSearch,
  setPage,
  onEdit,
  onViewSubTypes,
  onPreviewMetadata,
  showSelection,
  selectedIds,
  onSelectionChange,
}: MasterDataDeductionTypesTableProps) => {
  const typesById = useMemo(() => {
    const map = new Map<number, MasterDataDeductionTypeEntity>();
    for (const entity of deductionTypes) {
      map.set(entity.typeId, entity);
    }
    return map;
  }, [deductionTypes]);

  const dataSource = useMemo(
    () => mapTypesToRows(deductionTypes),
    [deductionTypes],
  );

  const selectedRowKeys = useMemo(
    () => Array.from(selectedIds).map(String),
    [selectedIds],
  );

  const selectableKeys = useMemo(
    () =>
      deductionTypes
        .filter((entity) => !entity.isLinked)
        .map((entity) => String(entity.typeId)),
    [deductionTypes],
  );

  const handleSelectionChange = useCallback(
    (keys: Set<string>) => {
      const next = new Set<number>();
      for (const key of keys) {
        const id = Number(key);
        const entity = typesById.get(id);
        if (entity && !entity.isLinked) {
          next.add(id);
        }
      }
      onSelectionChange(next);
    },
    [onSelectionChange, typesById],
  );

  const columns = useMemo(() => {
    const dataColumns = buildColumns({
      onEdit,
      onViewSubTypes,
      onPreviewMetadata,
      typesById,
    });
    if (!showSelection) return dataColumns;
    return [
      createMasterDataLinkedSelectionColumn<MasterDataDeductionTypeRow>({
        selectedKeys: new Set(selectedRowKeys),
        selectableKeys,
        onChange: handleSelectionChange,
        getAriaLabel: (record) =>
          record.isLinked
            ? `${record.name} is linked and cannot be selected`
            : `Select ${record.name}`,
      }),
      ...dataColumns,
    ];
  }, [
    handleSelectionChange,
    onEdit,
    onPreviewMetadata,
    onViewSubTypes,
    selectableKeys,
    selectedRowKeys,
    showSelection,
    typesById,
  ]);

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="deduction types"
      ariaLabel="Deduction Types table"
      setSearch={setSearch}
      setPage={setPage}
    />
  );
};
